import { useCookie } from '#app';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { personService } from '~/services/person-service';

import { useVaultStore } from '~/stores/person/vault/vault';
import { ModalNames } from '~/types/modals';
import type { Address, Aka, License, Person } from '~/types/person/person';
import type { EmailVerificationData, Phone } from '~/types/person/vault';

export const usePersonStore = defineStore('person', () => {
    const { showSuccess, showError } = useGlobalToast();
    const person = ref<Person>();
    const auth = useAuthStore();
    const impersonationLoader = ref(false);
    const errorMessage = ref('');
    const imgViewerUrl = ref<string | null>(null);
    const impersonatedLicense = ref<string>('');
    const selectedPersonForLicense = ref<License | null>(null);

    const impersonating = computed(() => {
        // Check if we're impersonating by looking at the cookie
        const impersonateId = useCookie<string | null>('impersonate_id').value;
        const actualUserId = auth.user?.identity?.id;

        if (!impersonateId || !actualUserId) {
            return false;
        }

        const impersonateIdNum = parseInt(impersonateId);
        const result = impersonateIdNum !== actualUserId ? impersonateIdNum : false;
        return result;
    });

    async function endImpersonation() {
        useCookie('impersonate_id').value = null // Clear cookie

        await new Promise(resolve => setTimeout(resolve, 50));

        impersonationLoader.value = true;

        // Clear vault data when ending impersonation
        const { clearVaultData } = useVaultStore();
        clearVaultData();

        await getPerson();
        impersonationLoader.value = false;
    }

    async function startImpersonation(personId: number) {
        if ( personId === person.value?.id ) {
            return;
        }

        const cookie = useCookie('impersonate_id');
        cookie.value = String(personId); // Set cookie
        impersonationLoader.value = true;

        // Clear vault data when switching persons
        const { clearVaultData } = useVaultStore();
        clearVaultData();

        // Ensure cookie is set before proceeding
        await new Promise(resolve => setTimeout(resolve, 50));

        await getPerson();
        impersonationLoader.value = false;
    }

    // Fetch and set the original person data
    async function getPerson() {
        const authStore = useAuthStore();

        const impersonateId = useCookie<string | null>('impersonate_id').value
        const actualUserId = authStore.user?.identity?.id

        let personId = null;
        if (impersonateId && actualUserId && parseInt(impersonateId) !== actualUserId) {
            personId = parseInt(impersonateId)
        } else if (actualUserId) {
            personId = actualUserId
        }

        // Don't make API call if we still don't have a valid personId
        if (!personId) {
            console.warn('getPerson called without valid personId, skipping API call');
            return;
        }
        try {
            person.value = await personService.getPerson(personId);
        } catch (error: any) {
            // During SSR, if we can't fetch person data due to auth issues, just log and continue
            if (process.server && error.message?.includes('SSR without authentication')) {
                return;
            }
            throw error;
        }
    }

    /**
     * Update the person data (dob, first_name, last_name, middle_name)
     * @param postData - The data to update (can be a partial of the Person type)
     */
    async function updatePerson(postData: Partial<Person>) {

        if (!person.value?.id) return;

        try {
            await personService.updatePerson(person.value.id, postData);
            await getPerson();
            showSuccess("Success", 'Person updated successfully');
        } catch (error: any) {
            showError('Error', error.message || 'Failed to update person');
        }
    }

    /**
     * Add an alias name to the person
     * @param postData - The alias name to add
     * @returns A toast message
     */
    async function addAka(postData: Aka) {
        if (!person.value?.id) return;

        try {
            await personService.addAka(person.value.id, postData);
            await getPerson();
            await auth.getMe();
            showSuccess("Success", 'Alias name added');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to add alias name');
        }
    }

    /**
     * Delete an alias name from the person
     * @param akaId - The id of the alias name to delete
     * @returns A toast message
     */
    async function deleteAka(akaId: number) {
        if (!person.value?.id) return;

        try {
            await personService.deleteAka(person.value.id, akaId);
            await getPerson();
            showSuccess("Success", 'Alias name deleted');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to delete alias name');
        }
    }

    /**
     * Add an address to the person
     * @param postData - The address to add
     * @returns A toast message
     */
    async function addAddress(postData: Address) {
        if (!person.value?.id) return;

        try {
            await personService.addAddress(person.value.id, postData);
            await getPerson();
            showSuccess("Success", 'Address added');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to add address');
        }
    }

    /**
     * Update an address
     * @param addressId - The id of the address to update
     * @param postData - The data to update
     * @returns A toast message
     */
    async function updateAddress(addressId: number, postData: Partial<Address>) {
        if (!person.value?.id) return;

        try {
            await personService.updateAddress(person.value.id, addressId, postData);
            await getPerson();
        } catch (error: any) {
            showError("Error", error.message || 'Failed to update address');
        }
    }

    /**
     * Delete an address from the person
     * @param addressId - The id of the address to delete
     * @returns A toast message
     */
    async function deleteAddress(addressId: number) {
        if (!person.value?.id) return;

        try {
            await personService.deleteAddress(person.value.id, addressId);
            await getPerson();
            showSuccess("Success", 'Address deleted');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to delete address');
        }
    }

    /**
     * Delete a phone number from the person
     * @param phoneId - The id of the phone number to delete
     * @returns A toast message
     */
    async function deletePhone(phoneId: number) {
        if (!person.value?.id) return;

        try {
            await personService.deletePhone(person.value.id, phoneId);
            await getPerson();
            showSuccess("Success", 'Phone number deleted');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to delete phone number');
        }
    }

    /**
     * Update a phone number
     * @param phoneId - The id of the phone number to update
     * @param postData - The data to update
     * @returns A toast message
     */
    async function updatePhone(phoneId: number, postData: Partial<Phone>) {
        if (!person.value?.id) return;

        try {
            await personService.updatePhone(person.value.id, phoneId, postData);
            await getPerson();
            showSuccess("Success", 'Phone number updated');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to update phone number');
        }
    }

    /**
     * Send a verification code to a phone number
     * @param postData - The phone number to send the verification code to
     * @returns A toast message
     */
    async function sendPhoneVerificationCode(

        postData: { phone_number: string; country_code: string; method: string; fromExposure?: boolean; exposureData?: any },
        isResend: boolean = false
    ) {
        if (!person.value?.id) return;

        try {
            await personService.sendPhoneVerificationCode(person.value.id, postData);

            if (!isResend) {
                useDynamicDialog(ModalNames.PHONE_VERIFICATION, postData);
            }

            showSuccess("Success", 'Phone number added');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to send phone verification code');
        }
    }

    /**
     * Verify the OTP code and add the phone number to the person
     * @param otpValue - The OTP code to verify
     * @returns A toast message
     */
    async function verifyPhoneOtp(payload: { country_code: string; phone_number: string; code: string }) {
        if (!person.value?.id) return;

        try {
            await personService.verifyPhoneOtp(person.value.id, payload);
            await getPerson();

            showSuccess("Success", 'Phone number added');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to verify phone OTP');
            throw error;
        }
    }

    /**
     * Send a verification code to an email and open the PIN modal
     * @param email - The email to send the verification code to
     * @returns A toast message
     */
    async function sendEmailVerificationCode(email: string, isResend: boolean = false, additionalData?: any) {
        try {
            await personService.sendEmailVerificationCode(email);

            if (!isResend) {
                useDynamicDialog(ModalNames.EMAIL_VERIFICATION, { email, ...additionalData });
            }

            showSuccess("Success", 'Email verification code sent');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to send email verification code');
            throw error;
        }
    }

    /**
     * Verify the OTP code and add the email to the person
     * @param otpValue - The OTP code to verify
     * @returns A toast message
     */
    async function verifyEmailOtp(payload: EmailVerificationData) {
        if (!person.value?.id) return;

        try {
            await personService.verifyEmailOtp(person.value.id, payload);
            await getPerson();
            showSuccess("Success", 'Email added');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to verify email OTP');
            errorMessage.value = error.message
            throw error;
        }
    }

    /**
     * Update an email
     * @param emailId - The id of the email to update
     * @param postData - The data to update
     * @returns A toast message
     */
    async function updateEmail(emailId: number, postData: { primary: boolean }) {
        if (!person.value?.id) return;

        try {
            await personService.updateEmail(person.value.id, emailId, postData);
            await getPerson();
        } catch (error: any) {
            showError("Error", error.message || 'Failed to update email');
        }
    }

    /**
     * Delete an email from the person
     * @param emailId - The id of the email to delete
     * @returns A toast message
     */
    async function deleteEmail(emailId: number) {
        if (!person.value?.id) return;

        try {
            await personService.deleteEmail(person.value.id, emailId);
            await getPerson();
            showSuccess("Success", 'Email deleted');
        } catch (error: any) {
            showError("Error", error.message || 'Failed to delete email');
        }
    }

    async function fetchStates(id: number) {
        if (!person.value?.id) return;

        try {
           const data = await personService.fetchStates(id);

           return data;
        } catch (error: any) {
            showError("Error", error.message || 'Failed to fetch states');
        }
    }

    async function fetchStatesByCode(countryCode: string) {
        if (!person.value?.id) return;

        try {
           const data = await personService.fetchStatesByCode(countryCode);

           return data;
        } catch (error: any) {
            showError("Error", error.message || 'Failed to fetch states');
        }
    }

    return {
        person,
        getPerson,
        updatePerson,
        addAka,
        deleteAka,
        addAddress,
        deleteAddress,
        deletePhone,
        deleteEmail,
        sendEmailVerificationCode,
        verifyEmailOtp,
        sendPhoneVerificationCode,
        verifyPhoneOtp,
        updateAddress,
        updatePhone,
        updateEmail,
        impersonationLoader,
        imgViewerUrl,
        selectedPersonForLicense,
        impersonating,
        impersonatedLicense,
        startImpersonation,
        endImpersonation,
        fetchStates,
        fetchStatesByCode,
        errorMessage
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(usePersonStore, import.meta.hot))
  }
